import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  LOCALE_ID,
  OnInit,
  Signal,
  signal,
  WritableSignal
} from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  capitalize,
  CustomLineSkeletonComponent,
  IconComponent,
  isUserPosWorklistRightPvmo,
  isUserRvExemptionRightPvmo,
  KeyValueLabel,
  NavbarComponent,
  NoValue,
  toKeyValueWithKeyLabel,
  TRANSLATE_SERVICE_TOKEN,
  TranslateInterface
} from '@tribeauto/ngx-mobilite-material';
import { ActivatedRoute, Params, Router, RouterOutlet } from '@angular/router';
import { ContractActionService } from './contract-action.service';
import { ReferenceStore } from '../../reference/reference-store.service';
import { ReferenceService } from '../../reference/reference.service';
import { ActionButtonEnum } from './action-list.model';
import { ApplicationStore } from '../../../core/services/application-store.service';
import {
  deliveryAvailableList,
  documentsAvailableList,
  exemptionVrAvailableList,
  fileHistoryAvailableList,
  messagingAvailableList,
  signatureAvailableList
} from './contract-action.model';
import { MessagingService } from './messaging/messaging.service';
import { filter, finalize, forkJoin, map, of, switchMap, tap, timer } from 'rxjs';
import { mapRecapCardList } from '../../layout/recap-card/recap-card.mapper';
import { ContractResponse, CreditHolderType } from './contract-response.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RecordsService } from '../records/records.service';
import { QueryParamsEnum } from '../callback/callback.model';

@Component({
  selector: 'pvmo-contract-action-hub',
  imports: [IconComponent, RouterOutlet, NavbarComponent, CustomLineSkeletonComponent, TranslatePipe],
  providers: [
    {
      provide: TRANSLATE_SERVICE_TOKEN,
      useFactory: (translateService: TranslateService): TranslateInterface => ({
        get: (key: string): string => translateService.instant(key)
      }),
      deps: [TranslateService]
    }
  ],
  templateUrl: './contract-action.component.html',
  styleUrl: './contract-action.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContractActionComponent implements OnInit {
  private readonly locale = inject(LOCALE_ID);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly contractActionService = inject(ContractActionService);
  private readonly messagingService = inject(MessagingService);
  private readonly referenceService = inject(ReferenceService);
  private readonly referenceStore = inject(ReferenceStore);
  private readonly applicationStore = inject(ApplicationStore);
  private readonly translateService = inject(TranslateService);
  private readonly recordsService = inject(RecordsService);
  private readonly activatedRoute = inject(ActivatedRoute);

  id = input.required<string>();
  notificationsCount: Signal<number> = computed(() => {
    return this.messagingService.lastNotificationsCount();
  });
  navbarTabList = toKeyValueWithKeyLabel(ActionButtonEnum).filter(
    keyValue =>
      ![
        ActionButtonEnum.DECISION.toString(),
        ActionButtonEnum.DUPLICATION.toString(),
        ActionButtonEnum.CANCELLATION.toString()
      ].includes(keyValue.value)
  );
  disabledNavbarTabKeyList: WritableSignal<string[]> = signal([]);
  clientName = signal('');

  tabLoader = signal(true);

  ngOnInit(): void {
    this.contractActionService
      .fetchContractDerogationRecapByNumAuto(this.id())
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((contract: ContractResponse | null) => {
          const actionsData = this.applicationStore.instant('actionsData')();
          if (this.id() !== actionsData?.cckNumber && contract?.pointOfSale.vendorNumber) {
            const recordsRequest = {
              cckNumbers: [this.id()],
              caller: 'F',
              partnerInfo: { webStoreIds: [contract.pointOfSale.vendorNumber] },
              userLogin: isUserPosWorklistRightPvmo(this.applicationStore.instant('userData')()?.userRights)
                ? null
                : this.applicationStore.instant('userData')()?.jossoLogin
            };
            return this.recordsService.getFilesList(recordsRequest).pipe(
              tap(recordsResult => {
                this.applicationStore.update('actionsData', {
                  cckNumber: this.id(),
                  actions: recordsResult?.myFileResponses?.[0]?.actions
                });
              })
            );
          }
          return of(contract);
        }),
        finalize(() => {
          this.setDisabledNavbarTabKeyList();
          this.tabLoader.set(false);
        })
      )
      .subscribe();

    if (this.referenceStore.isStoreFull()) {
      this.fetchContractRecap();
      this.setTimerGetChatMessages();
    } else {
      this.referenceService
        .fetchAllReferences()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.fetchContractRecap();
          this.setTimerGetChatMessages();
        });
    }
  }

  back(): void {
    void this.router.navigate(['/records'], { queryParams: this.getWorkListQueryParams() });
  }

  changeAction(action: KeyValueLabel): void {
    void this.router.navigate([`/contract-action-hub/${this.id()}/${action.value}`], {
      queryParams: this.activatedRoute.snapshot.queryParams
    });
  }

  getActualPathKeyValueLabel(): KeyValueLabel {
    const actualPath = this.getActualPath(this.router.url.split('?')[0]);
    const currentKeyValuePath = this.navbarTabList.find(tab => tab.value === actualPath);
    return currentKeyValuePath || this.navbarTabList[0];
  }

  private getActualPath(hostPath: string): string {
    const splittedHostPath = hostPath.split('/');
    return splittedHostPath.at(-1) ?? '';
  }

  private getWorkListQueryParams(): Params {
    const workListFilters = this.applicationStore.instant('workListFilters')();
    const params: Params = {};
    if (workListFilters?.index) {
      params[QueryParamsEnum.INDEX] = workListFilters.index.toString();
    }
    if (workListFilters?.pos) {
      params[QueryParamsEnum.POS] = workListFilters.pos;
    }
    if (workListFilters?.seller) {
      params[QueryParamsEnum.SELLER] = workListFilters.seller;
    }
    if (workListFilters?.cckNumber) {
      params[QueryParamsEnum.CCK_NUMBER] = workListFilters.cckNumber;
    }
    if (workListFilters?.clientName) {
      params[QueryParamsEnum.CLIENT_NAME] = workListFilters.clientName;
    }
    if (workListFilters?.sortDirection) {
      params[QueryParamsEnum.SORT_PROPERTY] = workListFilters.sortProperty;
    }
    if (workListFilters?.sortDirection) {
      params[QueryParamsEnum.SORT_DIRECTION] = workListFilters.sortDirection;
    }
    return params;
  }

  private setTimerGetChatMessages(): void {
    timer(0, 30000)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => this.messagingService.getChatMessages(this.id()))
      )
      .subscribe();
  }

  private fetchContractRecap(): void {
    this.contractActionService
      .fetchContractRecapListByNumAuto(this.id())
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(response => !!response),
        tap(contractResponse => this.contractActionService.updateStore(contractResponse)),
        switchMap(contractResponse => {
          this.setClientName(contractResponse);
          if (contractResponse.holder.physicalPerson?.profession.giro) {
            const giro = contractResponse.holder.physicalPerson.profession.giro;
            const giroCoHolder = contractResponse.coHolder?.physicalPerson?.profession.giro;
            return forkJoin([
              this.referenceService.fetchProfessionsBySectorKey(giro),
              giroCoHolder ? this.referenceService.fetchProfessionsBySectorKey(giroCoHolder) : of([]),
              this.referenceService.getContractsForProfessionAndEntryDate(
                giro,
                contractResponse.holder.physicalPerson.profession.professionCode
              )
            ]).pipe(
              map(([professionGiroResponse, professionCoGiroResponse, professionCodeResponse]) => {
                this.referenceStore.setProfessionCode([...professionGiroResponse, ...professionCoGiroResponse]);
                this.referenceStore.setContractCode(professionCodeResponse);
                return mapRecapCardList(
                  contractResponse,
                  this.referenceStore.referenceList(),
                  this.locale,
                  this.translateService
                );
              })
            );
          } else if (contractResponse.holder.type === CreditHolderType.SOCIETY) {
            return of(
              mapRecapCardList(
                contractResponse,
                this.referenceStore.referenceList(),
                this.locale,
                this.translateService
              )
            );
          } else {
            return of(null);
          }
        })
      )
      .subscribe(response => {
        if (response) {
          this.contractActionService.lastFetchedRecapCardList.set(response);
        }
      });
  }

  private setClientName(contractResponse: ContractResponse): void {
    if (contractResponse.holder.physicalPerson) {
      this.clientName.set(
        capitalize(contractResponse.holder.physicalPerson.firstName).concat(
          ' ',
          contractResponse.holder.physicalPerson.lastName.toUpperCase()
        )
      );
    } else if (contractResponse.holder.legalEntity) {
      this.clientName.set(contractResponse.holder.legalEntity.company.name.toUpperCase());
    }
  }

  private setDisabledNavbarTabKeyList(): void {
    const actionsData = this.applicationStore.instant('actionsData')();
    if (this.id() === actionsData?.cckNumber) {
      this.disableAction(messagingAvailableList, actionsData.actions, ActionButtonEnum.MESSAGING.toUpperCase());
      this.disableAction(documentsAvailableList, actionsData.actions, ActionButtonEnum.DOCUMENTS.toUpperCase());
      this.disableAction(signatureAvailableList, actionsData.actions, ActionButtonEnum.SIGNATURE.toUpperCase());
      this.disableAction(fileHistoryAvailableList, actionsData.actions, ActionButtonEnum.FILEHISTORY.toUpperCase());
      this.disableAction(deliveryAvailableList, actionsData.actions, ActionButtonEnum.DELIVERY.toUpperCase());
      this.disableAction(exemptionVrAvailableList, actionsData.actions, ActionButtonEnum.EXEMPTIONVR.toUpperCase());
    }
  }

  private disableAction(availableList: string[], actionsList: string[] | NoValue, actionToDisable: string): void {
    const disabledNavBar = this.disabledNavbarTabKeyList();
    if (
      !availableList.some(action => actionsList?.includes(action)) ||
      (actionToDisable === ActionButtonEnum.EXEMPTIONVR.toUpperCase() &&
        !isUserRvExemptionRightPvmo(this.applicationStore.instant('userData')()?.userRights))
    ) {
      disabledNavBar.push(actionToDisable);
      this.disabledNavbarTabKeyList.set(disabledNavBar);
    }
  }
}
